import { useState, useRef } from 'react';
import { Upload, Loader2, User } from 'lucide-react';
import { uploadAgentAvatar, type AgentType } from '../../services/settingsService';

interface AgentAvatarUploadProps {
  agentType: AgentType;
  avatarUrl: string | null;
  onAvatarChange: (url: string) => void;
}

export function AgentAvatarUpload({ agentType, avatarUrl, onAvatarChange }: AgentAvatarUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be under 5MB');
      return;
    }

    setUploading(true);
    try {
      const url = await uploadAgentAvatar(agentType, file);
      if (url) {
        setImageError(false);
        onAvatarChange(url);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="relative group cursor-pointer" onClick={() => !uploading && fileInputRef.current?.click()}>
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1875FF] via-[#05E0D1] to-[#52B5AE] flex items-center justify-center overflow-hidden ring-4 ring-[#05E0D1]/40 group-hover:ring-[#05E0D1]/60 transition-all duration-300 shadow-2xl group-hover:scale-105">
        {uploading ? (
          <Loader2 className="w-7 h-7 text-white animate-spin" />
        ) : avatarUrl && !imageError ? (
          <img
            src={avatarUrl}
            alt="Agent avatar"
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <User className="w-9 h-9 text-white" />
        )}
      </div>
      <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
        {uploading ? (
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        ) : (
          <Upload className="w-6 h-6 text-white" />
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
}
